import * as Yup from 'yup';
import i18n from '../../../../../services/i18n';
export interface TranslationMap {
  [key: string]: string;
}

export interface CounselingStage {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface CounselingType {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface PatientResponse {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface ConcessionReason {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface CancelReason {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface PaymentMethod {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface UpiProvider {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface OnlinePaymentMode {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface Category {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface CodeType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface SacCode {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface ClassName {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface DurationType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface DistributionType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface ServiceSourceType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}

export interface TariffOption {
  id: number;
  tariff_id: number;
  tariff_name: string;
  tariff_code: string;
}

export interface TariffSourceGroupOption {
  label: string;
  value: string;
  tariffs: TariffOption[];
}

const requiredMessage = () =>
  i18n.t('validation.required', { ns: 'Packagebilling', defaultValue: 'Required' });
const nonNegativeMessage = () =>
  i18n.t('validation.non_negative', { ns: 'Packagebilling', defaultValue: 'Value must be 0 or greater' });
const minOneMessage = () =>
  i18n.t('validation.min_one', { ns: 'Packagebilling', defaultValue: 'Value must be at least 1' });
const percentRangeMessage = () =>
  i18n.t('validation.percent_range', { ns: 'Packagebilling', defaultValue: 'Value must be between 0 and 100' });
const amountMismatchMessage = () =>
  i18n.t('validation.amounts_must_match', {
    ns: 'Packagebilling',
    defaultValue: 'Service Amount + Pharmacy Amount must equal Package Amount',
  });

export const validationSchema = Yup.object({
  package_concession_amount: Yup.number().min(0, nonNegativeMessage),
  package_details: Yup.object({
    patient: Yup.number().required(requiredMessage),
    package: Yup.number().required(requiredMessage),
    billing_source: Yup.string().required(requiredMessage),
    billing_source_id: Yup.number(),
    tariff: Yup.string().required(requiredMessage),
    tariff_name: Yup.string(),
    duration_type: Yup.string().required(requiredMessage),
    distribution_type: Yup.string().required(requiredMessage),
    package_name_snapshot: Yup.string(),
    package_amount_snapshot: Yup.number(),
    package_duration: Yup.number().required(requiredMessage).min(1, minOneMessage),
    pharmacy_amount: Yup.number().required(requiredMessage).min(0, nonNegativeMessage),
    package_amount: Yup.number().required(requiredMessage).min(0, nonNegativeMessage),
    service_amount: Yup.number().required(requiredMessage).min(0, nonNegativeMessage),
    pharmacy_percent: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage),
    service_percent: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage),
    package_net_amount: Yup.number().min(0, nonNegativeMessage),
    remark: Yup.string(),
    is_finalized: Yup.boolean(),
  }).test(
    'amounts-match',
    amountMismatchMessage,
    function (value) {
      const serviceAmount = Number(value?.service_amount) || 0;
      const pharmacyAmount = Number(value?.pharmacy_amount) || 0;
      const packageAmount = Number(value?.package_amount) || 0;
      // Allow small floating point tolerance
      return Math.abs((serviceAmount + pharmacyAmount) - packageAmount) < 0.01;
    }
  ),
  service_items: Yup.array().of(
    Yup.object({
      service: Yup.number().required(requiredMessage),
      service_name: Yup.string().required(requiredMessage),
      service_source_type: Yup.string(),
      procedure: Yup.string().nullable(),
      category: Yup.string(),
      gender: Yup.string().required(requiredMessage),
      discount_percent: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage).required(requiredMessage),
      discount_amount: Yup.number().min(0, nonNegativeMessage).required(requiredMessage),
      discount_on_quantity: Yup.boolean(),
      unlimited_qty: Yup.boolean(),
      quantity: Yup.number().when('unlimited_qty', {
        is: true,
        then: schema => schema.min(0, nonNegativeMessage).notRequired(),
        otherwise: schema => schema.min(1, minOneMessage).required(requiredMessage),
      }),
      unit_rate: Yup.number().min(0, nonNegativeMessage).required(requiredMessage),
      unit_rate_percentage: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage),
      gross_amount: Yup.number().min(0, nonNegativeMessage),
      total_amount: Yup.number().min(0, nonNegativeMessage),
      individual_discount_amount: Yup.number().min(0, nonNegativeMessage),
      individual_discount_percent: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage),
      distributed_concession_amount: Yup.number().min(0, nonNegativeMessage),
    })
  ),
  pharmacy_items: Yup.array().of(
    Yup.object({
      item: Yup.number().notRequired(),
      item_name: Yup.string().notRequired(),
      quantity: Yup.number().notRequired().nullable(),
      uom: Yup.string().notRequired(),
      discount_percent: Yup.number().min(0, nonNegativeMessage).max(100, percentRangeMessage),
      total_amount: Yup.number().min(0, nonNegativeMessage),
    })
  ).nullable(),
});
export interface PharmacyItem {
  id?: number;
  item: number;
  item_name?: string;
  uom?: number | null;
  uom_description?: string | null;
  quantity: number;
  discount_percent: string | number;
  total_amount?: string | number;
  uom_dropdown?: UomConversion[] | null;
  uom_conversion?: UomConversion[] | null;
}


export interface ServiceItem {
  unlimited_qty?: boolean | undefined;
  unlimited_quantity?: boolean | undefined;
  id?: number | null;
  service_id?: number;
  service: number;
  service_name: string;
  service_source_type: string;
  procedure: string | null;
  category: string;
  gender: string;
  discount_percent: string | number;
  discount_on_quantity?: boolean;
  discount_on_qty?: boolean;
  quantity: number | string;
  unit_rate: string | number;
  unit_rate_percentage?: string | number;
  unit_percentage?: string | number;
  gross_amount: string | number;
  total_amount: string | number;
  discount_amount?: string | number;
  // Individual service discount (manually entered, not modified by package concession)
  individual_discount_amount?: string | number;
  individual_discount_percent?: string | number;
  // Distributed package concession for this service (calculated from package concession)
  distributed_concession_amount?: string | number;
  is_from_package?: boolean;
  is_prescribed_from_doctor?: boolean;
  original_unit_rate?: string | number;
  original_quantity?: string | number;
  // Track which procedures/cycles this service came from
  procedure_details?: Array<{
    id?: number;
    procedure_id: number;
    procedure_source_type?: string;
    procedure_source_name?: string;
    procedure_source_name_label?: string;
  }>;
}

export interface Meta{
  matched_count: number;
  total_count: number;
  package_only_count: number;
  doctor_prescribed_only_count: number;
}
export interface ProcedureDetailItem {
  id?: number;
  procedure_id: number;
  procedure_source_type: string;
  procedure_source_name: string;
}

export interface PackageFormValues {
  id?: number;
  package_concession_amount?: string | number;
  package_details: {
    id?: number;
    patient: number;
    package: number;
    counseling_session?: number | null;
    package_name?: string;
    package_amount?: number | string;
    billing_source: string;
    tariff: number | null;
    tariff_name: string;
    package_duration: number;
    duration_type: string;
    distribution_type: string | null;
    service_amount: string | number;
    service_pct?: string | number;
    pharmacy_amount: string | number;
    pharmacy_pct?: string | number;
    total_service_amount?: string | number;
    package_gross_amount?: string | number;
    package_concession_amount?: string | number;
    total_service_concession_amount?: string | number;
    total_service_concession_percent?: string | number;
    package_net_amount?: string | number;
    remark: string;
    is_active?: boolean;
    is_finalized: boolean;
    created_on?: string;
    updated_on?: string;
    created_by?: number;
    updated_by?: number;
    // Extra fields in interface but not in payload are optional
    package_name_snapshot?: string;
    package_amount_snapshot?: number;
    pharmacy_percent?: number;
    service_percent?: number;
    // Package level adjustable head and consumables
    adjustable_head?: boolean;
    is_consumables?: boolean;
    consumable_amount?: number | string;
    // Pharmacy set all items flag
    pharmacy_set_all_items?: boolean;
    // Procedure source fields
    procedure_source_type?: string;
    procedure_source_id?: number;
    procedure_source_name?: string;
    procedure_details?: ProcedureDetailItem[];
  };
  service_items: ServiceItem[];
  pharmacy_items: PharmacyItem[];
  meta?: Meta;
}

export const initialValues: PackageFormValues = {
  package_concession_amount: 0,
  package_details: {
    counseling_session: null,
    patient: 0,
    package: 0,
    package_name: '',
    package_amount: 0,
    billing_source: '',
    tariff: null,
    tariff_name: '',
    duration_type: '',
    distribution_type: null,
    package_duration: 0,
    pharmacy_amount: 0,
    pharmacy_percent: 0,
    service_amount: 0,
    service_percent: 0,
    remark: '',
    is_finalized: false,
    adjustable_head: false,
    is_consumables: false,
    consumable_amount: 0,
    pharmacy_set_all_items: false,
    procedure_details: [],
  },
  service_items: [
    {
      service: 0,
      service_name: '',
      service_source_type: '',
      procedure: '',
      category: '',
      gender: '',
      discount_percent: 0,
      discount_amount: 0,
      individual_discount_amount: 0,
      individual_discount_percent: 0,
      distributed_concession_amount: 0,
      discount_on_quantity: false,
      unlimited_qty: false,
      quantity: 1,
      unit_rate: 0,
      gross_amount: 0,
      total_amount: 0,
      is_from_package: false,
      is_prescribed_from_doctor: false
    },
  ],
  pharmacy_items: [
    {
      item: 0,
      item_name: '',
      quantity: 1,
      uom: null,
      discount_percent: 0,
    },
  ],
};



export interface PackageLevel {
  id: number;
  level_code: string;
  level_name: string;
}

export interface PackageClassRate {
  id: number;
  class_name: string;
  rate: string;
}

export interface PackageRule {
  id: number;
  rule_code: string;
  rule_name: string;
  rule_description: string;
  active: boolean;
  min_percent: string | null;
  max_percent: string | null;
  percentage: string | null;
  amount: string | null;
  min_amount: string | null;
  max_amount: string | null;
}

export interface PackageDetails {
  id: number;
  code: string;
  code_type: string;
  code_details: string;
  sac_code: string;
  package_name: string;
  short_description: string;
  long_description: string;
  package_amount: string;
  effective_date: string;
  expiry_date: string;
  status: boolean;
  freeze: boolean;
  levels: PackageLevel[];
  class_rates: PackageClassRate[];
  rules: PackageRule[];
  is_open?: boolean;
  patient_package_id?: number | null;
}
export interface meta{
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
 }
export interface PackageListResponse {
  meta: meta;
  objects: PackageDetails[]; // Add this line for the actual package data
}


export interface PharmacyBatch {
  id: number;
  batch_number: string;
  expiry_date: string;
}

export interface UomConversion {
  id: number | null;
  from_uom: number;
  from_uom_label: string;
  conversion_factor: number;
}

export interface PharmacyItemDetails {
  id: number;
  code: string;
  name: string;
  brand_name: string;
  cost_price: string;
  mrp: string;
  is_active: boolean;
  is_perishable: boolean;
  inclusive_of_tax: boolean;
  is_free: boolean;
  is_stimulation_drug: boolean;
  is_trigger_drug: boolean;
  manufacturing_company: number;
  marketed_by: string;
  min_expiry_in_days: number;
  storage_temperature: string | null;
  strength: string | null;
  category: string;
  group: string;
  molecule: string;
  molecule_label: string;
  stock_uom: string;
  stock_uom_label: string | null;
  dispensing_type: string;
  pregnancy_class: string | null;
  route: string;
  storage_type: string | null;
  strength_type: string | null;
  therapeutic_class: string | null;
  batches: PharmacyBatch[];
  taxes: unknown[]; // Adjust type if you have tax details
  uom_conversion: UomConversion[];
  cost_price_with_tax: number;
  mrp_with_tax: number;
  name_label: string;
  brand_name_label: string;
}

export interface PharmacyListResponse {
  meta: meta;
  objects: PharmacyItemDetails[];
}


// TypeScript interfaces for the provided configuration structure

export interface BillingSource {
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface CounselingStage {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}

export interface CounselingType {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}

export interface PatientResponse {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}

export interface ItemGroup {
  name: string;
  code: string;
}

export interface ItemCategory {
  name: string;
  code: string;
}

export interface UOM {
  id: number;
  name: string;
  code: string;
}
export interface PackageProcedureMaster {
  name: string;
  code: string;
  translation: TranslationMap;
}
export interface billingConfigResponse {
  COUNSELING_STAGE: CounselingStage[];
  COUNSELING_TYPE: CounselingType[];
  PATIENT_RESPONSE: PatientResponse[];
  CONCESSION_REASON: ConcessionReason[];
  CANCEL_REASON: CancelReason[];
  PAYMENT_METHOD: PaymentMethod[];
  UPI_PROVIDER: UpiProvider[];
  ONLINE_PAYMENT_MODE: OnlinePaymentMode[];
  CATEGORY: Category[];
  CODE_TYPE: CodeType[];
  SAC_CODE: SacCode[];
  CLASS_NAME: ClassName[];
  BILLING_SOURCE: BillingSource[];
  DURATION_TYPE: DurationType[];
  DISTRIBUTION_TYPE: DistributionType[];
  SERVICE_SOURCE_TYPE: ServiceSourceType[];
  item_group: ItemGroup[];
  item_category: ItemCategory[];
  uom: UOM[];
  tariff_source_group: TariffSourceGroupOption[];
  PACKAGE_PROCEDURE_MASTER: PackageProcedureMaster[];

}

export interface ServiceMasterItem {
  id: number;
  category: string;
  code: string;
  charges: number;
  name: string;
  source: string;
  service_type: string;
  procedure: string;
}

export function getPackageListURL(): string {
  return 'packages/';
}

export function getPharmacyListURL(): string {
  return 'item/?is_drug=true';
} 

export function getbillingConfigURL(): string {
  return 'billing/billing-config-options/';
}

export function getServicesURL(): string {
  return 'billing/search-services/';
}
export function PackageDetailsURL(): string {
  return 'patient-packages/';
}

export function getPackageDetailsURL(): string {
  return 'patient-packages/counseling';
}

export function getOpenPackageServicesURL(patientPackageId: number): string {
  return `patient-packages/${patientPackageId}/`;
}

export function putPackageDetailsURL(): string {
  return 'patient-packages/';
}

/**
 * Distributes the total package concession across services proportionally
 * based on each service's contribution to the total service amount.
 * 
 * IMPORTANT: This preserves individual_discount_amount and ADDS the distributed
 * concession on top of it to calculate the effective discount_amount.
 * 
 * Combined discount formula:
 * - discount_amount = individual_discount_amount + distributed_concession_amount (per unit)
 * - discount_percent = (discount_amount / unit_rate) * 100
 * - gross_service_amount = unit_rate * quantity
 * - net_service_amount = gross_service_amount - (discount_amount * quantity)
 *
 * @param services - Array of service items
 * @param totalConcession - Total package concession amount to distribute
 * @returns Array of service items with updated discount fields and total_amount
 */
export function distributeConcessionToServices(
  services: ServiceItem[],
  totalConcession: number
): ServiceItem[] {
  if (!services || services.length === 0) {
    return services;
  }
  
  if (totalConcession <= 0) {
    // No package concession - effective discount is just individual discount
    return services.map(s => {
      const unitRate = Number(s.unit_rate) || 0;
      const quantity = Number(s.quantity) || 0;
      const grossAmount = unitRate * quantity;
      const individualDiscountAmt = Number(s.individual_discount_amount) || 0;
      // Total discount for entire service = individual_discount_amount * quantity (since individual is per-unit)
      const totalIndividualDiscount = individualDiscountAmt * quantity;
      const finalDiscountAmount = totalIndividualDiscount;
      const effectiveDiscountAmt = individualDiscountAmt; // per unit
      // Effective discount percent per unit
      const effectiveDiscountPct = unitRate > 0 
        ? Number(((effectiveDiscountAmt / unitRate) * 100).toFixed(2)) 
        : 0;
      const totalAmount = Number((grossAmount - finalDiscountAmount).toFixed(2));
      return { 
        ...s, 
        distributed_concession_amount: 0,
        discount_amount: effectiveDiscountAmt, 
        discount_percent: effectiveDiscountPct,
        gross_amount: grossAmount,
        total_amount: totalAmount 
      };
    });
  }

  // Calculate total service base amount (unit_rate * quantity for each service)
  const servicesWithBase = services.map((service, originalIdx) => {
    const unitRate = Number(service.unit_rate) || 0;
    const quantity = Number(service.quantity) || 0;
    const baseAmount = unitRate * quantity;
    const individualDiscountAmt = Number(service.individual_discount_amount) || 0;
    return { service, baseAmount, quantity, unitRate, originalIdx, individualDiscountAmt };
  });

  const totalBaseAmount = servicesWithBase.reduce((sum, s) => sum + s.baseAmount, 0);

  if (totalBaseAmount <= 0) {
    // No base amount to distribute - effective discount is just individual discount
    return services.map(s => {
      const unitRate = Number(s.unit_rate) || 0;
      const quantity = Number(s.quantity) || 0;
      const grossAmount = unitRate * quantity;
      const individualDiscountAmt = Number(s.individual_discount_amount) || 0;
      const totalIndividualDiscount = individualDiscountAmt * quantity;
      const finalDiscountAmount = totalIndividualDiscount;
      const effectiveDiscountAmt = individualDiscountAmt; // per unit
      const totalAmount = Number((grossAmount - finalDiscountAmount).toFixed(2));
      return { 
        ...s, 
        distributed_concession_amount: 0,
        discount_amount: effectiveDiscountAmt, 
        discount_percent: 0, // No share when totalBaseAmount is 0
        gross_amount: grossAmount,
        total_amount: totalAmount 
      };
    });
  }

  // Separate services with valid base amounts for distribution
  const validServices = servicesWithBase.filter(s => s.baseAmount > 0 && s.quantity > 0);
  
  // Calculate proportional concession for all valid services first
  // originalIdx -> serviceConcession (total for the service, not per-unit)
  const concessionMap = new Map<number, number>();
  
  // First pass: calculate proportional shares for all services
  validServices.forEach((item) => {
    const sharePercent = item.baseAmount / totalBaseAmount;
    const serviceConcession = Number((totalConcession * sharePercent).toFixed(2));
    concessionMap.set(item.originalIdx, serviceConcession);
  });
  
  // Calculate total distributed and find rounding drift
  let distributedTotal = 0;
  concessionMap.forEach(value => { distributedTotal += value; });
  const roundingDrift = Number((totalConcession - distributedTotal).toFixed(2));
  
  // Fix rounding drift by adjusting the service with the largest gross amount
  if (roundingDrift !== 0 && validServices.length > 0) {
    // Find service with largest gross
    let largestGrossIdx = validServices[0].originalIdx;
    let largestGross = validServices[0].baseAmount;
    validServices.forEach(item => {
      if (item.baseAmount > largestGross) {
        largestGross = item.baseAmount;
        largestGrossIdx = item.originalIdx;
      }
    });
    
    // Apply drift to largest gross service
    const currentConcession = concessionMap.get(largestGrossIdx) ?? 0;
    concessionMap.set(largestGrossIdx, Number((currentConcession + roundingDrift).toFixed(2)));
  }

  // Build updated services array preserving original order
  // Add distributed concession to individual discount to get effective discount
  const updatedServices = servicesWithBase.map(item => {
    const serviceConcession = concessionMap.get(item.originalIdx) ?? 0;
    const distributedPerUnit = item.quantity > 0 
      ? Number((serviceConcession / item.quantity).toFixed(2)) 
      : 0;
    
    // Total individual discount for entire service
    const totalIndividualDiscount = item.individualDiscountAmt * item.quantity;
    
    // Final combined discount = individual total + package concession
    const finalDiscountAmount = Number((totalIndividualDiscount + serviceConcession).toFixed(2));
    
    // Gross amount for this service
    const grossAmount = item.baseAmount;
    
    // Effective discount per unit = individual + distributed per unit
    const effectiveDiscountAmt = Number((item.individualDiscountAmt + distributedPerUnit).toFixed(2));
    
    // Effective discount percent per unit
    const effectiveDiscountPct = item.unitRate > 0 
      ? Number(((effectiveDiscountAmt / item.unitRate) * 100).toFixed(2)) 
      : 0;
    
    // total_amount = gross - (discount_amount * qty) = net service amount
    const totalAmount = Number((grossAmount - finalDiscountAmount).toFixed(2));
    
    return {
      ...item.service,
      distributed_concession_amount: distributedPerUnit,
      discount_amount: effectiveDiscountAmt,
      discount_percent: effectiveDiscountPct,
      gross_amount: grossAmount,
      total_amount: totalAmount,
    };
  });

  return updatedServices;
}